<template>
  <div class="message-processing-page" :class="{ embedded }">
    <div class="container">
      <div v-if="!embedded" class="page-header">
        <router-link to="/tickets?tab=messages" class="page-title-link">
          <h1 class="page-title">{{ $t('messageProcessing.title') }}</h1>
          <p class="page-subtitle">{{ $t('messageProcessing.subtitle') }}</p>
        </router-link>
      </div>
      
      <!-- 消息列表区域 -->
      <div class="messages-section">
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">{{ $t('messageProcessing.messageList') }}</h3>
            <div class="message-tabs">
              <button 
                class="tab-btn" 
                :class="{ active: messageTab === 'unread' }"
                @click="changeTab('unread')"
              >
{{ $t('messageProcessing.unreadMessages') }}
                <span v-if="unreadCount > 0" class="unread-badge">{{ unreadCount }}</span>
              </button>
              <button 
                class="tab-btn" 
                :class="{ active: messageTab === 'read' }"
                @click="changeTab('read')"
              >
{{ $t('messageProcessing.readMessages') }}
              </button>
            </div>
            <div class="header-actions">
              <button class="btn btn-secondary" @click="refreshMessages" :disabled="loading">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M1 4V10H7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M3.51001 15C4.01716 16.4332 4.87906 17.7161 6.01543 18.727C7.15181 19.7378 8.52549 20.4432 10.0083 20.7808C11.4911 21.1185 13.0348 21.0781 14.4952 20.6632C15.9556 20.2483 17.2853 19.4732 18.36 18.41" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M23 20V14H17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M20.49 9C19.9828 7.56678 19.1209 6.28392 17.9845 5.27304C16.8482 4.26216 15.4745 3.55685 13.9917 3.21916C12.5089 2.88146 10.9652 2.92188 9.50481 3.33681C8.04439 3.75174 6.71475 4.52677 5.64 5.59" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
{{ $t('common.refresh') }}
              </button>
            </div>
          </div>
          
          <div class="messages-list" v-if="!loading && messages.length > 0">
            <div 
              v-for="message in messages" 
              :key="message.id"
              class="message-item"
              :class="{ 'unread-message': messageTab === 'unread' }"
              @click="openMessage(message)"
            >
              <div class="message-header">
                <div class="message-meta">
                  <span class="sender">{{ message.pname || $t('messageProcessing.systemMessage') }}</span>
                  <span class="time">{{ formatTime(message.ptime) }}</span>
                  <span v-if="messageTab === 'unread'" class="unread-indicator">{{ $t('messageProcessing.unreadStatus') }}</span>
                </div>
                <div class="message-title">
                  {{ message.title }}
                </div>
              </div>
              
              <div class="message-content" v-html="message.content"></div>
            </div>
          </div>
          
          <div v-else-if="loading" class="loading-state">
            <div class="spinner"></div>
            <p>{{ $t('messageProcessing.loadingMessages') }}</p>
          </div>
          
          <div v-else class="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
              <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <p>{{ $t('messageProcessing.noMessages') }}</p>
          </div>
          
          <!-- 分页 -->
          <div class="pagination" v-if="!loading && messages.length > 0">
            <button 
              class="page-btn" 
              :disabled="pagination.currentPage <= 1"
              @click="changePage(pagination.currentPage - 1)"
            >
{{ $t('common.previous') }}
            </button>
            <span class="page-info">
{{ $t('common.pageInfo', { current: pagination.currentPage, total: pagination.totalPages }) }}
            </span>
            <button 
              class="page-btn" 
              :disabled="pagination.currentPage >= pagination.totalPages"
              @click="changePage(pagination.currentPage + 1)"
            >
{{ $t('common.next') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    
  </div>
</template>

<script>
import { ref, onMounted, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { getUserMessages, markUserMessageAsRead } from '@/api/sms'
import { isAuthenticated } from '@/api/user'
import { useRouter, useRoute } from 'vue-router'

export default {
  name: 'MessageProcessingView',
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const { t: $t } = useI18n()
    const messages = ref([])
    const loading = ref(false)
    const unreadCount = ref(0)
    const messageTab = ref('unread') // 'unread' 或 'read'

    const pagination = reactive({
      currentPage: 1,
      pageSize: 30,
      totalPages: 1,
      total: 0,
      pageSizes: [10, 20, 50, 100, 500, 1000, 5000]
    })
    
    // 检查登录状态
    const checkAuth = () => {
      if (!isAuthenticated()) {
        ElMessage.warning($t('messages.loginRequired'))
        router.push('/login')
        return false
      }
      return true
    }
    
    // 获取消息列表
    const fetchMessages = async () => {
      if (!checkAuth()) return
      
      try {
        loading.value = true
        const params = {
          page: pagination.currentPage,
          pageSize: pagination.pageSize,
          pageSizes: pagination.pageSizes,
          total: pagination.total,
          unread: messageTab.value === 'unread'
        }
        
        const res = await getUserMessages(params)
        if (res.success) {
          messages.value = res.data.list || []
          pagination.total = res.data.total || 0
          pagination.totalPages = Math.ceil(pagination.total / pagination.pageSize)
          
          // 如果当前是未读消息标签，更新未读消息数量
          if (messageTab.value === 'unread') {
            unreadCount.value = res.data.total || 0
          } else {
            // 如果在已读标签，需要单独获取未读消息数量
            fetchUnreadCount()
          }
        }
      } catch (error) {
        console.error($t('console.fetchMessagesFailed'), error)
      } finally {
        loading.value = false
      }
    }
    
    // 单独获取未读消息数量
    const fetchUnreadCount = async () => {
      try {
        const res = await getUserMessages({
          page: 1,
          pageSize: 1,
          unread: true
        })
        
        if (res.success) {
          unreadCount.value = res.data.total || 0
        }
      } catch (error) {
        console.error($t('console.fetchUnreadCountFailed'), error)
      }
    }
    
    // 切换消息标签
    const changeTab = (tab) => {
      if (messageTab.value === tab) return
      
      messageTab.value = tab
      pagination.currentPage = 1
      fetchMessages()
    }
    
    // 刷新消息
    const refreshMessages = () => {
      fetchMessages()
    }
    
    // 换页
    const changePage = (page) => {
      pagination.currentPage = page
      fetchMessages()
    }
    
    // 格式化时间
    const formatTime = (timestamp) => {
      if (!timestamp) return '--'
      const date = new Date(timestamp)
      return date.toLocaleString('zh-CN')
    }
    
    // 打开消息时标记为已读
    const openMessage = async (message) => {
      if (!checkAuth()) return
      
      // 只有在未读消息标签下并且当前消息未读时，才标记为已读
      if (messageTab.value === 'unread') {
        try {
          // 标记消息为已读
          await markUserMessageAsRead(message.id)
          
          // 从当前列表中移除该消息
          messages.value = messages.value.filter(m => m.id !== message.id)
          
          // 更新未读消息数量
          if (unreadCount.value > 0) {
            unreadCount.value -= 1
          }
          window.dispatchEvent(new CustomEvent('support-unread-refresh'))
          
          // 如果消息列表为空且还有更多页，则自动加载下一页
          if (messages.value.length === 0 && pagination.currentPage < pagination.totalPages) {
            pagination.currentPage += 1
            fetchMessages()
          } else if (messages.value.length === 0) {
            // 如果没有更多消息，重新获取未读消息数量
            fetchUnreadCount()
          }
        } catch (error) {
          console.error($t('console.markMessageReadFailed'), error)
        }
      }
    }
    
    onMounted(() => {
      fetchMessages()
      
      // 兼容旧问题反馈链接，转到新工单中心。
      if (route.query.feedback === 'true') {
        router.replace('/tickets?create=true')
      }
    })
    
    return {
      messages,
      loading,
      messageTab,
      pagination,
      unreadCount,
      changeTab,
      refreshMessages,
      changePage,
      formatTime,
      openMessage
    }
  }
}
</script>

<style scoped>
.message-processing-page {
  padding: 40px 0;
  min-height: calc(100vh - var(--header-height) - var(--footer-height));
}

.message-processing-page.embedded {
  min-height: auto;
  padding-top: 24px;
}

.page-header {
  text-align: center;
  margin-bottom: 40px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 12px;
  color: var(--text-primary);
  background: linear-gradient(90deg, #4461F2 0%, #38BFFF 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
}

.page-title-link {
  text-decoration: none;
  display: block;
  transition: transform 0.2s;
}

.page-title-link:hover {
  transform: translateY(-2px);
}

.feedback-widget {
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 100;
}

.feedback-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: linear-gradient(90deg, #4461F2 0%, #38BFFF 100%);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(68, 97, 242, 0.3);
  transition: all 0.2s;
}

.feedback-button:hover {
  opacity: 0.9;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(68, 97, 242, 0.4);
}

.messages-section .card {
  background-color: var(--card-background);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  padding: 40px;
  border: 1px solid var(--border-color);
}

.messages-section .card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  margin-bottom: 24px;
  border-bottom: none;
  flex-wrap: wrap;
  gap: 16px;
}

.card-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  position: relative;
  padding-left: 16px;
}

.card-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 20px;
  background: linear-gradient(180deg, #4461F2 0%, #38BFFF 100%);
  border-radius: 2px;
}

.message-tabs {
  display: flex;
  gap: 8px;
}

.tab-btn {
  position: relative;
  padding: 8px 16px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  background-color: var(--card-background);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.tab-btn.active {
  background-color: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.unread-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  background-color: #EF4444;
  color: white;
  font-size: 12px;
  font-weight: 600;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.btn-secondary {
  padding: 10px 20px;
  background: rgba(68, 97, 242, 0.1);
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-secondary:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-2px);
  background: rgba(68, 97, 242, 0.15);
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: linear-gradient(90deg, #4461F2 0%, #38BFFF 100%);
  color: white;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-2px);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.messages-list {
  padding: 0;
}

.message-item {
  padding: 20px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border-color);
  margin-bottom: 16px;
  background-color: var(--card-background);
  transition: all 0.2s;
  cursor: pointer;
}

.unread-message {
  border-left: 3px solid var(--primary-color);
  background-color: rgba(68, 97, 242, 0.03);
}

.message-item:hover {
  border-color: var(--primary-color);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.unread-indicator {
  font-size: 12px;
  font-weight: 600;
  color: var(--primary-color);
  background-color: rgba(68, 97, 242, 0.1);
  padding: 2px 8px;
  border-radius: 12px;
}

.message-header {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}

.message-meta {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
}

.message-title {
  font-weight: 600;
  font-size: 16px;
  color: var(--text-primary);
}

.sender {
  font-weight: 600;
  color: var(--text-primary);
}

.time {
  font-size: 14px;
  color: var(--text-tertiary);
}

.message-content {
  line-height: 1.5;
  color: var(--text-secondary);
}

.loading-state, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--text-tertiary);
  background-color: rgba(68, 97, 242, 0.02);
  border-radius: var(--radius-md);
  border: 1px dashed var(--border-color);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--border-color);
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 24px 0 0;
  margin-top: 24px;
  border-top: 1px solid var(--border-color);
}

.page-btn {
  padding: 10px 20px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  background-color: rgba(68, 97, 242, 0.05);
  color: var(--text-primary);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background-color: rgba(68, 97, 242, 0.1);
  border-color: var(--primary-color);
  color: var(--primary-color);
  transform: translateY(-2px);
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  color: var(--text-secondary);
  font-size: 14px;
}

/* 弹窗样式 */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.dialog {
  background: var(--card-background);
  border-radius: var(--radius-lg);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow: hidden;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-color);
}

.dialog-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: var(--text-tertiary);
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.close-btn:hover {
  background-color: var(--hover-color);
  color: var(--text-primary);
}

.dialog-content {
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
}

.feedback-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.feedback-hint {
  background-color: #fefce8;
  border: 1px solid #facc15;
  border-radius: var(--radius-sm);
  padding: 16px;
  margin-bottom: 4px;
  border-left: 4px solid #facc15;
}

.hint-text {
  margin: 0 0 8px 0;
  font-size: 14px;
  line-height: 1.5;
  color: #92400e;
}

.hint-text:last-child {
  margin-bottom: 0;
}

.hint-text strong {
  color: #b45309;
  font-weight: 600;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.feedback-textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  font-size: 14px;
  resize: vertical;
  transition: all 0.2s;
}

.feedback-textarea:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(68, 97, 242, 0.1);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
}

.btn-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s linear infinite;
  margin-right: 8px;
}

/* 768px以下平板和大屏移动端适配 */
@media (max-width: 768px) {
  .message-processing-page {
    padding: 20px 0;
  }

  .page-title {
    font-size: 24px;
    margin-bottom: 8px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .messages-section .card-header {
    flex-direction: column;
    align-items: flex-start;
    padding: 16px 20px;
  }
  
  .card-title {
    font-size: 16px;
    margin-bottom: 12px;
  }

  .message-tabs {
    width: 100%;
    margin-bottom: 12px;
  }
  
  .tab-btn {
    flex: 1;
    text-align: center;
    padding: 10px 16px;
    font-size: 14px;
    min-height: 44px; /* 符合触摸标准 */
  }

  .unread-badge {
    position: static;
    margin-left: 8px;
    width: auto;
    height: auto;
    padding: 2px 6px;
    font-size: 11px;
    border-radius: 10px;
  }
  
  .header-actions {
    width: 100%;
  }
  
  .header-actions button {
    flex: 1;
    font-size: 14px;
    padding: 10px 16px;
    min-height: 44px;
  }

  .messages-list {
    padding: 16px;
  }

  .message-item {
    padding: 16px;
    margin-bottom: 12px;
    border-radius: var(--radius-md);
  }

  .message-meta {
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 6px;
  }

  .message-title {
    font-size: 15px;
    line-height: 1.4;
    margin-bottom: 8px;
  }

  .sender {
    font-size: 14px;
  }

  .time {
    font-size: 12px;
  }

  .unread-indicator {
    font-size: 11px;
    padding: 1px 6px;
  }

  .message-content {
    font-size: 14px;
    line-height: 1.5;
  }

  .pagination {
    flex-direction: column;
    gap: 12px;
    padding: 16px;
  }

  .page-btn {
    padding: 12px 20px;
    font-size: 16px;
    min-height: 44px;
  }
  
  .feedback-widget {
    bottom: 20px;
    right: 20px;
  }
  
  .feedback-button {
    padding: 10px 16px;
    font-size: 14px;
  }

  /* 弹窗适配 */
  .dialog {
    width: 95%;
    max-width: 450px;
    margin: 20px;
  }

  .dialog-header {
    padding: 16px 20px;
  }

  .dialog-header h3 {
    font-size: 16px;
  }

  .dialog-content {
    padding: 20px;
  }

  .feedback-hint {
    padding: 14px;
    margin-bottom: 6px;
  }

  .hint-text {
    font-size: 13px;
    margin-bottom: 6px;
  }

  .feedback-textarea {
    padding: 14px;
    font-size: 16px; /* 防止iOS缩放 */
    border-radius: var(--radius-md);
  }

  .form-actions .btn {
    padding: 12px 24px;
    font-size: 16px;
    min-height: 44px;
    border-radius: var(--radius-md);
  }
}

/* 480px以下移动端适配 */
@media (max-width: 480px) {
  .message-processing-page {
    padding: 16px 0;
  }

  .container {
    padding: 0 12px;
  }

  .page-header {
    margin-bottom: 30px;
  }

  .page-title {
    font-size: 20px;
  }

  .page-subtitle {
    font-size: 13px;
  }

  .messages-section .card-header {
    padding: 14px 16px;
  }

  .card-title {
    font-size: 15px;
    margin-bottom: 10px;
  }

  .tab-btn {
    padding: 8px 12px;
    font-size: 13px;
    min-height: 40px;
  }

  .header-actions button {
    font-size: 13px;
    padding: 8px 12px;
    min-height: 40px;
  }

  .messages-list {
    padding: 12px;
  }

  .message-item {
    padding: 12px;
    margin-bottom: 10px;
  }

  .message-header {
    margin-bottom: 10px;
  }

  .message-meta {
    gap: 6px;
    margin-bottom: 6px;
  }

  .message-title {
    font-size: 14px;
    margin-bottom: 6px;
  }

  .sender {
    font-size: 13px;
  }

  .time {
    font-size: 11px;
  }

  .unread-indicator {
    font-size: 10px;
    padding: 1px 4px;
  }

  .message-content {
    font-size: 13px;
    line-height: 1.4;
  }

  .pagination {
    padding: 12px;
  }

  .page-btn {
    padding: 10px 16px;
    font-size: 14px;
    min-height: 40px;
  }

  .page-info {
    font-size: 13px;
  }

  .feedback-widget {
    bottom: 16px;
    right: 16px;
  }

  .feedback-button {
    padding: 8px 14px;
    font-size: 13px;
    border-radius: 24px;
  }

  .feedback-button svg {
    width: 16px;
    height: 16px;
  }

  /* 弹窗适配 */
  .dialog {
    width: 100%;
    max-width: none;
    margin: 10px;
    max-height: 90vh;
  }

  .dialog-header {
    padding: 14px 16px;
  }

  .dialog-header h3 {
    font-size: 15px;
  }

  .dialog-content {
    padding: 16px;
    max-height: calc(90vh - 140px);
  }

  .feedback-hint {
    padding: 12px;
    margin-bottom: 4px;
  }

  .hint-text {
    font-size: 12px;
    margin-bottom: 4px;
  }

  .form-group {
    gap: 6px;
  }

  .form-label {
    font-size: 13px;
  }

  .feedback-textarea {
    padding: 12px;
    font-size: 15px;
    min-height: 120px;
  }

  .form-actions .btn {
    width: 100%;
    padding: 14px 20px;
    font-size: 15px;
    min-height: 48px;
  }

  .btn-spinner {
    width: 14px;
    height: 14px;
  }

  .loading-state, .empty-state {
    padding: 40px 16px;
  }

  .spinner {
    width: 36px;
    height: 36px;
  }
}

/* 360px以下小屏移动端适配 */
@media (max-width: 360px) {
  .container {
    padding: 0 8px;
  }

  .page-title {
    font-size: 18px;
  }

  .messages-section .card-header {
    padding: 12px 14px;
  }

  .card-title {
    font-size: 14px;
    margin-bottom: 8px;
  }

  .message-tabs {
    gap: 4px;
  }

  .tab-btn {
    padding: 6px 8px;
    font-size: 12px;
    min-height: 36px;
  }

  .header-actions button {
    font-size: 12px;
    padding: 6px 8px;
    min-height: 36px;
  }

  .messages-list {
    padding: 10px;
  }

  .message-item {
    padding: 10px;
    margin-bottom: 8px;
  }

  .message-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .message-title {
    font-size: 13px;
  }

  .sender {
    font-size: 12px;
  }

  .time {
    font-size: 10px;
  }

  .message-content {
    font-size: 12px;
    line-height: 1.3;
  }

  .feedback-widget {
    bottom: 12px;
    right: 12px;
  }

  .feedback-button {
    padding: 6px 10px;
    font-size: 12px;
    border-radius: 20px;
  }

  .feedback-button svg {
    width: 14px;
    height: 14px;
  }

  .dialog {
    margin: 8px;
    border-radius: var(--radius-sm);
  }

  .dialog-header {
    padding: 12px 14px;
  }

  .dialog-header h3 {
    font-size: 14px;
  }

  .close-btn {
    width: 28px;
    height: 28px;
    font-size: 20px;
  }

  .dialog-content {
    padding: 14px;
  }

  .feedback-hint {
    padding: 10px;
    margin-bottom: 3px;
  }

  .hint-text {
    font-size: 11px;
    margin-bottom: 3px;
  }

  .feedback-textarea {
    padding: 10px;
    font-size: 14px;
    min-height: 100px;
  }

  .form-actions .btn {
    padding: 12px 16px;
    font-size: 14px;
    min-height: 44px;
  }
}
</style>
